<script setup lang="ts">
import { CalendarDaysIcon, ChevronDownIcon, InformationCircleIcon } from '@heroicons/vue/24/solid';
import type { DateValue } from '@internationalized/date';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Calendar } from '@/components/ui/calendar';

/**
 * Schedule-for-later chip + popover, shared by the bulk issue bar and the
 * single-player inspector control. "now" = issue immediately; picking a date+time
 * queues the command for the dispatcher. The "player time" toggle reads the picked
 * wall-clock in each player's own zone (default) or the operator's.
 *
 * Pure presentation: the date/time/player-time are v-models and every derived value
 * (scheduledAt / label / inPast) is supplied by {@link useIssueCommandForm}.
 */
const date = defineModel<DateValue | undefined>('date');
const time = defineModel<string>('time', { required: true });
const usePlayerTime = defineModel<boolean>('usePlayerTime', { required: true });

defineProps<{
    minValue: DateValue;
    scheduledAt: Date | null;
    scheduledInPast: boolean;
    scheduleLabel: string;
    browserTimezone: string;
    browserTimezoneAbbreviation: string;
    /** Prefix for data-testids so multiple instances stay unique on the page. */
    testIdPrefix?: string;
}>();
</script>

<template>
    <Popover>
        <PopoverTrigger as-child>
            <button
                type="button"
                class="border-border/60 hover:bg-muted/40 text-foreground inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5"
                :data-testid="testIdPrefix ? `${testIdPrefix}-trigger` : undefined"
            >
                <CalendarDaysIcon class="size-3.5 shrink-0" aria-hidden="true" />
                <span :class="scheduledAt ? '' : 'text-muted-foreground'">{{ scheduleLabel }}</span>
                <ChevronDownIcon class="size-3.5 shrink-0" aria-hidden="true" />
            </button>
        </PopoverTrigger>
        <PopoverContent align="start" class="w-auto p-3">
            <Calendar v-model="date" :min-value="minValue" />
            <div class="border-border/60 mt-3 flex items-center justify-between gap-2 border-t pt-3">
                <label class="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                    Time
                    <input
                        v-model="time"
                        type="time"
                        class="border-border/60 rounded-md border bg-transparent px-2 py-1 text-xs"
                        :data-testid="testIdPrefix ? `${testIdPrefix}-time` : undefined"
                    />
                </label>
                <button
                    v-if="scheduledAt"
                    type="button"
                    class="text-muted-foreground hover:text-foreground text-xs"
                    :data-testid="testIdPrefix ? `${testIdPrefix}-clear` : undefined"
                    @click="date = undefined"
                >
                    Clear
                </button>
            </div>

            <!-- Player time: read the pick in each player's own zone (default), else the operator's. -->
            <div class="border-border/60 mt-3 flex items-center justify-between gap-3 border-t pt-3">
                <span class="inline-flex items-center gap-1.5 text-xs">
                    <span class="text-foreground">Player time</span>
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <button type="button" class="text-muted-foreground/70 hover:text-foreground" aria-label="About player time">
                                <InformationCircleIcon class="size-3.5" aria-hidden="true" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent class="max-w-60 text-xs">
                            Scheduled times are read in each player's own timezone. If player time is off, we
                            use your timezone ({{ browserTimezoneAbbreviation }} · {{ browserTimezone }}).
                        </TooltipContent>
                    </Tooltip>
                </span>
                <button
                    type="button"
                    role="switch"
                    :aria-checked="usePlayerTime"
                    :data-state="usePlayerTime ? 'on' : 'off'"
                    :data-testid="testIdPrefix ? `${testIdPrefix}-player-time` : undefined"
                    class="bg-muted-foreground/30 data-[state=on]:bg-primary relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors"
                    @click="usePlayerTime = !usePlayerTime"
                >
                    <span
                        :data-state="usePlayerTime ? 'on' : 'off'"
                        class="bg-background size-3 translate-x-0.5 rounded-full shadow transition-transform data-[state=on]:translate-x-3.5"
                    />
                </button>
            </div>

            <p
                v-if="scheduledInPast"
                class="text-destructive mt-2 text-xs"
                :data-testid="testIdPrefix ? `${testIdPrefix}-past` : undefined"
            >
                Pick a time in the future.
            </p>
        </PopoverContent>
    </Popover>
</template>
